/** Logical buttons the engine understands. Games never look at raw key codes. */
export type Action =
  | "up"
  | "down"
  | "left"
  | "right"
  | "confirm"
  | "cancel"
  | "menu"
  | "run"
  | "mute"
  | "lookLeft"
  | "lookRight"
  | "debug";

export const DIRECTIONS = ["up", "down", "left", "right"] as const;
export type Direction = (typeof DIRECTIONS)[number];

const KEY_MAP: Record<string, Action> = {
  ArrowUp: "up",
  KeyW: "up",
  ArrowDown: "down",
  KeyS: "down",
  ArrowLeft: "left",
  KeyA: "left",
  ArrowRight: "right",
  KeyD: "right",
  KeyZ: "confirm",
  Enter: "confirm",
  Space: "confirm",
  KeyX: "cancel",
  Escape: "cancel",
  Backspace: "cancel",
  KeyC: "menu",
  Tab: "menu",
  ShiftLeft: "run",
  ShiftRight: "run",
  KeyQ: "lookLeft",
  KeyE: "lookRight",
  KeyM: "mute",
  Backquote: "debug",
};

/** Movement intent on the ground plane, before the camera is taken into account. */
export interface MoveAxis {
  /** -1 left … 1 right */
  x: number;
  /** -1 back … 1 forward */
  y: number;
  /** 0 … 1 */
  magnitude: number;
}

/**
 * Edge-triggered input, plus an analog movement axis and mouse look.
 *
 * `held` is level state; `pressed` is true only on the frame the button went
 * down and is cleared by `endFrame`. Anything that reacts to a press should
 * `consume` it so a single keystroke cannot be handled twice by two layers in
 * the same frame (e.g. a menu and the scene behind it).
 */
export class Input {
  private heldSet = new Set<Action>();
  private pressedSet = new Set<Action>();
  private releasedSet = new Set<Action>();

  /** Analog stick contributed by touch, if any. */
  private touchMove: { x: number; y: number } | null = null;
  private touchLook = { x: 0, y: 0 };

  private lookDelta = { x: 0, y: 0 };
  private zoomDelta = 0;
  /** Set while the pointer is captured, so we can show the right hint. */
  pointerLocked = false;
  /** Turn off to stop the canvas grabbing the mouse (menus, cutscenes). */
  pointerLockEnabled = true;

  private readonly onKeyDown = (e: KeyboardEvent) => {
    const action = KEY_MAP[e.code];
    if (!action) return;
    // Space and the arrows scroll the page; Tab moves focus. We want none of that.
    if (e.code !== "Escape") e.preventDefault();
    if (e.repeat) return;
    this.heldSet.add(action);
    this.pressedSet.add(action);
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    const action = KEY_MAP[e.code];
    if (!action) return;
    this.heldSet.delete(action);
    this.releasedSet.add(action);
  };

  private readonly onBlur = () => {
    // Never leave a key stuck down because focus moved mid-press.
    this.heldSet.clear();
    this.touchMove = null;
  };

  constructor(private target: HTMLElement) {
    window.addEventListener("keydown", this.onKeyDown, { passive: false });
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    this.bindMouse();
    this.bindTouch();
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
  }

  held(action: Action): boolean {
    return this.heldSet.has(action);
  }

  pressed(action: Action): boolean {
    return this.pressedSet.has(action);
  }

  released(action: Action): boolean {
    return this.releasedSet.has(action);
  }

  /** Returns true once and swallows the press for the rest of the frame. */
  consume(action: Action): boolean {
    if (!this.pressedSet.has(action)) return false;
    this.pressedSet.delete(action);
    return true;
  }

  anyPressed(): boolean {
    return this.pressedSet.size > 0;
  }

  clearPressed(): void {
    this.pressedSet.clear();
  }

  /** First held direction, in a stable priority order. Menus use this. */
  heldDirection(): Direction | null {
    for (const d of DIRECTIONS) if (this.held(d)) return d;
    return null;
  }

  /** Movement intent: keyboard keys, or the touch stick if one is active. */
  moveAxis(): MoveAxis {
    let x = 0;
    let y = 0;
    if (this.held("left")) x -= 1;
    if (this.held("right")) x += 1;
    if (this.held("up")) y += 1;
    if (this.held("down")) y -= 1;

    if (this.touchMove) {
      x = this.touchMove.x;
      y = this.touchMove.y;
    }

    const magnitude = Math.min(1, Math.hypot(x, y));
    if (magnitude === 0) return { x: 0, y: 0, magnitude: 0 };
    const length = Math.hypot(x, y);
    return { x: x / length, y: y / length, magnitude };
  }

  /** Accumulated look input for this frame, in radians. */
  look(): { x: number; y: number } {
    const keyboard = (this.held("lookRight") ? 1 : 0) - (this.held("lookLeft") ? 1 : 0);
    return {
      x: this.lookDelta.x + this.touchLook.x + keyboard * 0.035,
      y: this.lookDelta.y + this.touchLook.y,
    };
  }

  zoom(): number {
    return this.zoomDelta;
  }

  endFrame(): void {
    this.pressedSet.clear();
    this.releasedSet.clear();
    this.lookDelta.x = 0;
    this.lookDelta.y = 0;
    this.touchLook.x = 0;
    this.touchLook.y = 0;
    this.zoomDelta = 0;
  }

  /** Inject a press from something that is not the keyboard (touch, UI button). */
  press(action: Action): void {
    this.pressedSet.add(action);
  }

  requestPointerLock(): void {
    if (!this.pointerLockEnabled || this.pointerLocked) return;
    void this.target.requestPointerLock?.();
  }

  exitPointerLock(): void {
    if (document.pointerLockElement === this.target) document.exitPointerLock();
  }

  // ------------------------------------------------------------------ mouse

  private bindMouse(): void {
    const SENSITIVITY = 0.0028;

    this.target.addEventListener("mousedown", (e) => {
      if (e.button === 0) this.requestPointerLock();
    });

    document.addEventListener("pointerlockchange", () => {
      this.pointerLocked = document.pointerLockElement === this.target;
    });

    window.addEventListener("mousemove", (e) => {
      // Look while the pointer is captured, or while a button is held down —
      // so the game is playable whether or not you accept the pointer lock.
      const dragging = e.buttons !== 0 && e.target === this.target;
      if (!this.pointerLocked && !dragging) return;
      this.lookDelta.x += e.movementX * SENSITIVITY;
      this.lookDelta.y += e.movementY * SENSITIVITY;
    });

    this.target.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.zoomDelta += Math.sign(e.deltaY) * 0.6;
      },
      { passive: false },
    );

    this.target.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  // ------------------------------------------------------------------ touch

  /**
   * Twin stick: a drag starting on the left half of the screen steers, a drag
   * on the right half looks around, and a tap that never moves is "confirm".
   */
  private bindTouch(): void {
    const DEAD_ZONE = 14;
    const RANGE = 70;
    const LOOK_SENSITIVITY = 0.006;

    interface Touch {
      id: number;
      kind: "move" | "look";
      ox: number;
      oy: number;
      lx: number;
      ly: number;
      moved: boolean;
    }
    const touches = new Map<number, Touch>();

    this.target.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "mouse") return;
      const kind = e.clientX < window.innerWidth / 2 ? "move" : "look";
      touches.set(e.pointerId, {
        id: e.pointerId,
        kind,
        ox: e.clientX,
        oy: e.clientY,
        lx: e.clientX,
        ly: e.clientY,
        moved: false,
      });
    });

    this.target.addEventListener("pointermove", (e) => {
      const touch = touches.get(e.pointerId);
      if (!touch) return;

      if (touch.kind === "look") {
        this.touchLook.x += (e.clientX - touch.lx) * LOOK_SENSITIVITY;
        this.touchLook.y += (e.clientY - touch.ly) * LOOK_SENSITIVITY;
        touch.lx = e.clientX;
        touch.ly = e.clientY;
        if (Math.hypot(e.clientX - touch.ox, e.clientY - touch.oy) > DEAD_ZONE) touch.moved = true;
        return;
      }

      const dx = e.clientX - touch.ox;
      const dy = e.clientY - touch.oy;
      const distance = Math.hypot(dx, dy);
      if (distance < DEAD_ZONE) {
        this.touchMove = null;
        return;
      }
      touch.moved = true;
      const scale = Math.min(1, distance / RANGE) / distance;
      this.touchMove = { x: dx * scale, y: -dy * scale };
    });

    const end = (e: PointerEvent) => {
      const touch = touches.get(e.pointerId);
      if (!touch) return;
      if (!touch.moved) this.press("confirm");
      if (touch.kind === "move") this.touchMove = null;
      touches.delete(e.pointerId);
    };
    this.target.addEventListener("pointerup", end);
    this.target.addEventListener("pointercancel", end);
  }
}
